"use strict";
var EPBridgeAgentBundle = (() => {
  var __defProp = Object.defineProperty;
  var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
  var __getOwnPropNames = Object.getOwnPropertyNames;
  var __hasOwnProp = Object.prototype.hasOwnProperty;
  var __export = (target, all) => {
    for (var name in all)
      __defProp(target, name, { get: all[name], enumerable: true });
  };
  var __copyProps = (to, from, except, desc) => {
    if (from && typeof from === "object" || typeof from === "function") {
      for (let key of __getOwnPropNames(from))
        if (!__hasOwnProp.call(to, key) && key !== except)
          __defProp(to, key, { get: () => from[key], enumerable: !(desc = __getOwnPropDesc(from, key)) || desc.enumerable });
    }
    return to;
  };
  var __toCommonJS = (mod) => __copyProps(__defProp({}, "__esModule", { value: true }), mod);

  // poc/preview-bridge/preview.ts
  var preview_exports = {};
  __export(preview_exports, {
    PreviewBridgeAgent: () => PreviewBridgeAgent
  });

  // poc/sdk/src/locator.ts
  function resolveTarget(target) {
    const expected = target.expectMatchCount ?? 1;
    let nodes = [];
    try {
      nodes = Array.from(document.querySelectorAll(target.selector));
    } catch {
      return { target, status: "invalid_selector", elements: [], expected, found: 0 };
    }
    if (nodes.length === 0) return { target, status: "not_found", elements: [], expected, found: 0 };
    if (nodes.length !== expected) {
      return { target, status: "match_count_mismatch", elements: nodes, expected, found: nodes.length };
    }
    return { target, status: "ok", elements: nodes, expected, found: nodes.length };
  }

  // poc/sdk/src/operations.ts
  var STYLE_WHITELIST = /* @__PURE__ */ new Set([
    "color",
    "background-color",
    "font-family",
    "font-size",
    "font-style",
    "font-weight",
    "letter-spacing",
    "line-height",
    "text-align",
    "text-transform",
    "text-decoration",
    "opacity",
    "padding",
    "padding-top",
    "padding-right",
    "padding-bottom",
    "padding-left",
    "margin",
    "margin-top",
    "margin-right",
    "margin-bottom",
    "margin-left",
    "border-radius",
    "border-color",
    "border-width",
    "border-style",
    "max-width",
    "min-width",
    "width",
    "height",
    "object-fit"
  ]);
  var PROTECTED_SELECTORS = [".price", ".product__price", "[data-ep-protected]", "input", "textarea", "select", "form"];
  var UNSAFE_VALUE = /(javascript:|expression\(|url\s*\(|@import|[;}]|<\/)/i;
  function isProtected(element) {
    return PROTECTED_SELECTORS.some((sel) => {
      try {
        return element.matches(sel);
      } catch {
        return false;
      }
    });
  }
  function isSafeHref(value) {
    const v = value.trim();
    if (!v) return false;
    if (v.startsWith("/") || v.startsWith("#")) return !v.startsWith("//");
    return /^https?:\/\//i.test(v);
  }
  function setText(element, text) {
    element.textContent = text;
    if (text.includes("\n") && element instanceof HTMLElement) element.style.whiteSpace = "pre-line";
  }
  var OperationRuntime = class {
    constructor(device, onDiagnostic, maxReplays = 20) {
      this.device = device;
      this.onDiagnostic = onDiagnostic;
      this.maxReplays = maxReplays;
      this.applied = /* @__PURE__ */ new Map();
      this.order = [];
    }
    applyAll(operations) {
      return [...operations].sort((a, b) => a.sequence - b.sequence).map((op) => this.apply(op));
    }
    apply(op) {
      const base = { opId: op.id, targetId: op.target.targetId, type: op.type };
      if (op.status === "invalid") return { ...base, status: "skipped", reason: "operation_marked_invalid" };
      if (this.applied.has(op.id)) return { ...base, status: "skipped", reason: "already_applied" };
      if (op.deviceScope && op.deviceScope.length > 0 && !op.deviceScope.includes(this.device)) {
        return { ...base, status: "device_skipped", reason: `out_of_scope_${this.device}` };
      }
      const resolved = resolveTarget(op.target);
      if (resolved.status === "invalid_selector") return { ...base, status: "failed", reason: "invalid_selector" };
      if (resolved.status === "not_found") return { ...base, status: "failed", reason: "locator_not_found" };
      if (resolved.status === "match_count_mismatch") {
        if (resolved.elements.length > 0 && resolved.elements.every((el2) => isProtected(el2))) {
          this.onDiagnostic("operation_blocked_protected_target", {
            op_id: op.id,
            target_id: op.target.targetId,
            type: op.type,
            matches: resolved.found
          });
          return { ...base, status: "protected", reason: "all_matches_protected", found: resolved.found };
        }
        this.onDiagnostic("operation_locator_conflict", {
          op_id: op.id,
          target_id: op.target.targetId,
          locator_version: op.target.locatorVersion,
          selector: op.target.selector,
          expected: resolved.expected,
          found: resolved.found
        });
        return {
          ...base,
          status: "conflict",
          reason: "match_count_mismatch",
          expected: resolved.expected,
          found: resolved.found
        };
      }
      const element = resolved.elements[0];
      if (!element) return { ...base, status: "failed", reason: "locator_not_found" };
      if (isProtected(element)) {
        this.onDiagnostic("operation_blocked_protected_target", {
          op_id: op.id,
          target_id: op.target.targetId,
          type: op.type
        });
        return { ...base, status: "protected", reason: "protected_target" };
      }
      try {
        const record = this.writeElement(op, element);
        this.applied.set(op.id, record);
        this.order.push(op.id);
        return { ...base, status: "applied" };
      } catch (err) {
        const reason = err instanceof Error ? err.message : "apply_error";
        this.onDiagnostic("operation_apply_error", {
          op_id: op.id,
          target_id: op.target.targetId,
          message: reason
        });
        return { ...base, status: "failed", reason };
      }
    }
    writeElement(op, element) {
      switch (op.type) {
        case "text.replace": {
          const previous = element.textContent ?? "";
          const next = op.after ?? "";
          setText(element, next);
          return {
            op,
            element,
            replays: 0,
            effectPresent: () => (element.textContent ?? "") === next
          };
        }
        case "link.set": {
          const next = op.after ?? "";
          if (!isSafeHref(next)) throw new Error("unsafe_href");
          const previous = element.getAttribute("href");
          element.setAttribute("href", next);
          return {
            op,
            element,
            replays: 0,
            effectPresent: () => element.getAttribute("href") === next
          };
        }
        case "image.replace": {
          if (!(element instanceof HTMLImageElement)) throw new Error("not_an_image");
          const next = op.after ?? "";
          if (!isSafeHref(next)) throw new Error("unsafe_src");
          element.setAttribute("src", next);
          return { op, element, replays: 0, effectPresent: () => element.getAttribute("src") === next };
        }
        case "visibility.hide": {
          const previousDisplay = element.style.display;
          element.style.display = "none";
          element.setAttribute("data-ep-hidden", "true");
          return {
            op,
            element,
            replays: 0,
            effectPresent: () => element.getAttribute("data-ep-hidden") === "true" && element.style.display === "none"
          };
        }
        case "visibility.show": {
          const previousDisplay = element.style.display;
          element.removeAttribute("data-ep-hidden");
          element.style.display = previousDisplay === "none" ? "" : previousDisplay;
          return { op, element, replays: 0, effectPresent: () => !element.hasAttribute("data-ep-hidden") };
        }
        case "style.set": {
          const styles = op.styles ?? {};
          const applied = {};
          for (const [rawProp, rawValue] of Object.entries(styles)) {
            const prop = rawProp.trim().toLowerCase();
            const value = String(rawValue);
            if (!STYLE_WHITELIST.has(prop)) {
              this.onDiagnostic("operation_style_prop_blocked", { op_id: op.id, prop });
              continue;
            }
            if (UNSAFE_VALUE.test(value)) {
              this.onDiagnostic("operation_style_value_blocked", { op_id: op.id, prop });
              continue;
            }
            element.style.setProperty(prop, value);
            applied[prop] = element.style.getPropertyValue(prop);
          }
          if (Object.keys(applied).length === 0) throw new Error("no_applicable_style");
          return {
            op,
            element,
            replays: 0,
            effectPresent: () => {
              const style = element.style;
              return Object.entries(applied).every(([prop, expected]) => style.getPropertyValue(prop) === expected);
            }
          };
        }
        default: {
          throw new Error(`unsupported_operation_${String(op.type)}`);
        }
      }
    }
    /** Re-apply operations whose effect was wiped out by a framework re-render (e.g. Ajax Mini Cart). */
    replayIfNeeded() {
      let replays = 0;
      for (const opId of [...this.order]) {
        const record = this.applied.get(opId);
        if (!record) continue;
        if (record.effectPresent()) continue;
        if (record.replays >= this.maxReplays) {
          this.applied.delete(opId);
          this.order = this.order.filter((id) => id !== opId);
          this.onDiagnostic("operation_replay_limit", { op_id: opId, target_id: record.op.target.targetId, replays: record.replays });
          continue;
        }
        try {
          const refreshed = this.writeElement(record.op, record.element);
          this.applied.set(opId, { ...refreshed, replays: record.replays + 1 });
          replays += 1;
        } catch {
          this.applied.delete(opId);
          this.order = this.order.filter((id) => id !== opId);
        }
      }
      return replays;
    }
    /** Restore the Control experience: every applied operation is reverted in reverse order. */
    revertAll() {
      let reverted = 0;
      for (const opId of [...this.order].reverse()) {
        const record = this.applied.get(opId);
        if (!record) continue;
        const { op, element } = record;
        try {
          switch (op.type) {
            case "text.replace":
            case "link.set":
            case "image.replace":
            case "visibility.show":
              if (op.before !== void 0) {
                if (op.type === "text.replace") setText(element, op.before);
                else if (op.type === "link.set") element.setAttribute("href", op.before);
                else if (op.type === "image.replace") element.setAttribute("src", op.before);
              }
              break;
            case "visibility.hide":
              element.style.display = "";
              element.removeAttribute("data-ep-hidden");
              break;
            case "style.set":
              for (const prop of Object.keys(op.styles ?? {})) {
                element.style.removeProperty(prop.trim().toLowerCase());
              }
              break;
            default:
              break;
          }
          reverted += 1;
        } catch {
        }
        this.applied.delete(opId);
      }
      this.order = [];
      return reverted;
    }
    /** Elements that currently carry an applied operation, for impression/click instrumentation. */
    appliedTargets() {
      const out = [];
      const seen = /* @__PURE__ */ new Set();
      for (const record of this.applied.values()) {
        if (seen.has(record.op.target.targetId)) continue;
        seen.add(record.op.target.targetId);
        out.push({
          targetId: record.op.target.targetId,
          locatorVersion: record.op.target.locatorVersion,
          element: record.element
        });
      }
      return out;
    }
    get count() {
      return this.applied.size;
    }
  };

  // poc/sdk/src/util.ts
  function deviceType(width = window.innerWidth) {
    if (width < 768) return "mobile";
    if (width < 1024) return "tablet";
    return "desktop";
  }

  // poc/preview-bridge/inpage-editor.ts
  var CSS = `
.ep-ed-root{position:fixed;inset:0;pointer-events:none;z-index:2147483000;font:13px/1.55 -apple-system,BlinkMacSystemFont,"Segoe UI","PingFang SC","Microsoft YaHei",sans-serif;-webkit-font-smoothing:antialiased}
.ep-ed-bar{position:fixed;top:0;left:0;right:0;height:48px;background:#1b1d2a;color:#fff;display:flex;align-items:center;gap:10px;padding:0 14px;pointer-events:auto;box-shadow:0 2px 14px rgba(0,0,0,.28);z-index:3}
.ep-ed-bar .logo{width:22px;height:22px;border-radius:6px;background:#6c5ce7;display:grid;place-items:center;font-weight:700;font-size:12px}
.ep-ed-bar b{font-size:13px;font-weight:600}
.ep-ed-bar .tag{background:#6c5ce7;color:#fff;border-radius:99px;padding:2px 10px;font-size:11px;font-weight:600}
.ep-ed-bar .sp{flex:1}
.ep-ed-bar button{border:1px solid rgba(255,255,255,.2);background:rgba(255,255,255,.07);color:#fff;height:31px;padding:0 13px;border-radius:8px;font-size:12px;cursor:pointer;font-family:inherit}
.ep-ed-bar button:hover{background:rgba(255,255,255,.16)}
.ep-ed-bar button.primary{background:#6c5ce7;border-color:#6c5ce7;font-weight:600}
.ep-ed-bar button.primary:hover{background:#5a4bd1}
.ep-ed-bar button:disabled{opacity:.45;cursor:not-allowed}

/* \u60AC\u505C\u6846 */
.ep-ed-hl{position:absolute;border:2px dashed #6c5ce7;background:rgba(108,92,231,.07);border-radius:3px;pointer-events:none;display:none;z-index:2}
/* \u9009\u4E2D\u6846\uFF1A\u7C97\u5B9E\u7EBF + \u5143\u7D20\u540D\u6807\u7B7E */
.ep-ed-sel{position:absolute;border:3px solid #00b894;background:rgba(0,184,148,.06);border-radius:3px;pointer-events:none;display:none;z-index:2;box-shadow:0 0 0 1px rgba(0,184,148,.35)}
.ep-ed-sel .lbl{position:absolute;top:-22px;left:-3px;background:#00b894;color:#fff;font-size:10.5px;font-weight:700;padding:2px 8px;border-radius:5px 5px 0 0;white-space:nowrap;letter-spacing:.2px}
/* \u5DF2\u4FEE\u6539\u89D2\u6807 */
.ep-ed-modified{position:absolute;pointer-events:none;border:2px dashed #e17055;border-radius:3px;display:none;z-index:2}
.ep-ed-modified::after{content:'\u5DF2\u4FEE\u6539';position:absolute;top:-19px;right:-2px;background:#e17055;color:#fff;font-size:10px;font-weight:700;padding:1px 7px;border-radius:4px 4px 0 0}
/* \u5B9A\u4F4D\u95EA\u70C1 */
.ep-ed-flash{animation:epEdFlash .9s ease-out 2}
@keyframes epEdFlash{0%,100%{box-shadow:0 0 0 0 rgba(108,92,231,0)}40%{box-shadow:0 0 0 6px rgba(108,92,231,.55)}}

/* \u5DE6\u4FA7\u5143\u7D20\u6E05\u5355 */
.ep-ed-list{position:fixed;top:60px;left:12px;width:272px;max-height:calc(100vh - 76px);display:flex;flex-direction:column;background:#fff;border-radius:11px;box-shadow:0 16px 44px rgba(20,22,30,.22);pointer-events:auto;overflow:hidden;z-index:3}
.ep-ed-list header{padding:11px 12px 8px;border-bottom:1px solid #eef0f5;flex:0 0 auto}
.ep-ed-list header b{font-size:12.5px;color:#1f2430;display:block}
.ep-ed-list header .meta{color:#7b8190;font-size:11px;margin-top:2px}
.ep-ed-list .search{margin-top:8px}
.ep-ed-list .search input{width:100%;height:29px;border:1px solid #e6e8f0;border-radius:7px;padding:0 9px;font-size:12px;box-sizing:border-box;background:#fbfbfe;color:#1f2430;font-family:inherit}
.ep-ed-list .search input:focus{outline:none;border-color:#6c5ce7;box-shadow:0 0 0 3px rgba(108,92,231,.12)}
.ep-ed-list .body{overflow:auto;padding:6px 6px 10px}
.ep-ed-list .grp{font-size:10px;font-weight:700;color:#9aa1ae;letter-spacing:.6px;text-transform:uppercase;padding:8px 8px 4px}
.ep-ed-item{display:flex;gap:7px;align-items:flex-start;padding:6px 8px;border-radius:7px;cursor:pointer;border:1px solid transparent}
.ep-ed-item:hover{background:#f7f6ff;border-color:#e4e0fb}
.ep-ed-item.active{background:#eefbf7;border-color:#9fe6d3}
.ep-ed-item .k{font-size:9.5px;font-weight:700;color:#fff;background:#aab0bd;border-radius:4px;padding:1px 5px;flex:0 0 auto;margin-top:1px;text-transform:uppercase}
.ep-ed-item .k.img{background:#0984e3}
.ep-ed-item .k.link{background:#6c5ce7}
.ep-ed-item .k.btn{background:#00b894}
.ep-ed-item .k.h{background:#e17055}
.ep-ed-item .k.guard{background:#c0392b}
.ep-ed-item .txt{font-size:11.5px;color:#3b4252;overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;word-break:break-word}
.ep-ed-item .id{font-size:9.5px;color:#a4aab6;font-family:ui-monospace,Menlo,monospace;margin-top:1px}
.ep-ed-item.changed{background:#fff6f2;border-color:#f5cfc4}
.ep-ed-item.changed .txt::after{content:' \u25CF';color:#e17055;font-size:8px;vertical-align:super}

/* \u53F3\u4FA7\u5C5E\u6027\u9762\u677F */
.ep-ed-panel{position:fixed;top:60px;right:12px;width:300px;max-height:calc(100vh - 76px);overflow:auto;background:#fff;border-radius:11px;box-shadow:0 16px 44px rgba(20,22,30,.22);pointer-events:auto;padding:14px;z-index:3}
.ep-ed-panel h4{margin:0 0 3px;font-size:13px;color:#1f2430;display:flex;align-items:center;gap:6px}
.ep-ed-panel .kindtag{font-size:9.5px;font-weight:700;color:#fff;background:#6c5ce7;border-radius:4px;padding:1px 6px}
.ep-ed-panel .sub{color:#7b8190;font-size:11px;margin:0 0 10px;word-break:break-all;font-family:ui-monospace,Menlo,monospace}
.ep-ed-panel label{display:block;font-size:11.5px;font-weight:600;color:#3b4252;margin:9px 0 0}
.ep-ed-panel textarea,.ep-ed-panel input[type=text],.ep-ed-panel input[type=number]{width:100%;margin-top:4px;padding:6px 8px;border:1px solid #e6e8f0;border-radius:7px;font-size:12px;background:#fbfbfe;color:#1f2430;box-sizing:border-box;font-family:inherit}
.ep-ed-panel textarea{resize:vertical;min-height:56px}
.ep-ed-panel input[type=color]{width:100%;height:30px;margin-top:4px;border:1px solid #e6e8f0;border-radius:7px;background:#fbfbfe;padding:2px}
.ep-ed-panel .row{display:flex;gap:8px}
.ep-ed-panel .row>*{flex:1}
.ep-ed-panel .acts{display:flex;flex-wrap:wrap;gap:6px;margin-top:12px}
.ep-ed-panel .acts button{flex:1 1 44%;height:31px;border-radius:7px;border:1px solid #e6e8f0;background:#fbfbfe;color:#3b4252;font-size:12px;cursor:pointer;font-family:inherit}
.ep-ed-panel .acts button.primary{background:#6c5ce7;border-color:#6c5ce7;color:#fff;font-weight:600}
.ep-ed-panel .acts button:disabled{opacity:.5;cursor:not-allowed}
.ep-ed-guard{margin-top:10px;padding:9px 10px;border-radius:8px;background:#fdecea;color:#c0392b;font-size:11.5px;line-height:1.55}
.ep-ed-ops{margin-top:12px;border-top:1px solid #eef0f5;padding-top:9px}
.ep-ed-ops .ttl{font-size:11px;font-weight:700;color:#6b7280;margin-bottom:5px}
.ep-ed-ops .op{font-size:11px;color:#5b6270;padding:5px 6px;border-radius:6px;cursor:pointer;word-break:break-all;border:1px solid transparent}
.ep-ed-ops .op:hover{background:#f7f6ff;border-color:#e4e0fb}
.ep-ed-ops .op .mono{font-family:ui-monospace,Menlo,monospace;color:#3b4252}
.ep-ed-empty{color:#9aa1ae;font-size:11.5px}
.ep-ed-live{margin-top:9px;padding:7px 9px;border-radius:7px;background:#eefbf7;color:#0a7a5f;font-size:11px;line-height:1.55}
.ep-ed-toast{position:fixed;left:50%;bottom:26px;transform:translateX(-50%);background:#252735;color:#fff;padding:9px 15px;border-radius:8px;font-size:12px;pointer-events:auto;box-shadow:0 10px 30px rgba(0,0,0,.28);z-index:4}
.ep-ed-toast.err{background:#c0392b}
`;
  var GUARD_SELECTOR = '.price, [class*="price" i], [data-price], input, select, textarea, form, button[name="add"], [name="add"]';
  function el(tag, cls, text) {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text !== void 0) node.textContent = text;
    return node;
  }
  var InPageEditor = class {
    constructor(opts) {
      this.opts = opts;
      this.toastEl = null;
      this.ops = [];
      this.scanned = [];
      this.selected = null;
      this.hoverEl = null;
      this.mounted = false;
      this.saving = false;
      this.onMove = (ev) => {
        const hit = this.anchorOf(ev.target);
        const node = hit?.el ?? null;
        if (node === this.hoverEl) return;
        this.hoverEl = node;
        if (!node || node === this.selected?.element) {
          this.hl.style.display = "none";
          return;
        }
        this.place(this.hl, node);
      };
      this.onClick = (ev) => {
        if (this.root.contains(ev.target)) return;
        const hit = this.anchorOf(ev.target);
        if (!hit) return;
        ev.preventDefault();
        ev.stopPropagation();
        const item = this.scanned.find((s) => s.targetId === hit.id);
        if (item) this.selectItem(item, { scroll: false });
        else {
          const fallback = {
            targetId: hit.id,
            element: hit.el,
            kind: hit.el.tagName === "IMG" ? "image" : hit.el.tagName === "A" ? "link" : "text",
            text: textOf(hit.el),
            src: null,
            href: null,
            protectedNode: hit.el.matches(GUARD_SELECTOR) || Boolean(hit.el.closest(GUARD_SELECTOR)),
            section: "page"
          };
          this.scanned.push(fallback);
          this.renderList();
          this.selectItem(fallback, { scroll: false });
        }
      };
    }
    async mount() {
      if (this.mounted) return;
      this.mounted = true;
      const style = el("style");
      style.textContent = CSS;
      document.head.appendChild(style);
      this.root = el("div", "ep-ed-root");
      this.hl = el("div", "ep-ed-hl");
      this.selBox = el("div", "ep-ed-sel");
      this.modBox = el("div", "ep-ed-modified");
      this.root.append(this.hl, this.selBox, this.modBox);
      this.buildBar();
      this.buildList();
      this.buildPanel();
      document.body.appendChild(this.root);
      document.documentElement.style.paddingTop = "48px";
      this.scan();
      this.bindHover();
      this.bindClick();
      this.bindReposition();
      try {
        const res = await fetch(`${this.opts.session.platformOrigin}/api/v1/preview-sessions/${encodeURIComponent(this.opts.session.previewSessionId)}/operations`, {
          headers: { authorization: `Bearer ${this.opts.token}` }
        });
        if (res.ok) {
          const data = await res.json();
          this.ops = Array.isArray(data.operations) ? data.operations : [];
          this.renderList();
          this.renderOps();
          this.syncModifiedMarks();
          if (this.ops.length) this.toast(`\u5DF2\u8F7D\u5165 ${this.ops.length} \u6761\u5DF2\u4FDD\u5B58\u64CD\u4F5C\uFF08\u5DE6\u4FA7\u6E05\u5355\u91CC\u6807\u4E86"\u5DF2\u4FEE\u6539"\uFF09`);
        }
      } catch {
      }
    }
    destroy() {
      document.removeEventListener("mousemove", this.onMove, true);
      document.removeEventListener("click", this.onClick, true);
      this.root?.remove();
      document.documentElement.style.paddingTop = "";
      this.mounted = false;
      this.opts.onExit?.();
    }
    /* ------------------------------------------------------------------ 构建 */
    buildBar() {
      this.bar = el("div", "ep-ed-bar");
      const logo = el("div", "logo", "e");
      const title = el("b", "", "Experiment Studio \xB7 \u9875\u9762\u5185\u7F16\u8F91");
      const tag = el("span", "tag", `Variant ${this.opts.session.variantId} \xB7 0 \u6761\u64CD\u4F5C`);
      tag.setAttribute("data-role", "tag");
      const sp = el("div", "sp");
      const undoBtn = el("button", "", "\u21B6 \u64A4\u9500");
      const revertBtn = el("button", "", "\u6062\u590D\u539F\u6837");
      const saveBtn = el("button", "primary", "\u4FDD\u5B58\u5230 Variant");
      saveBtn.setAttribute("data-role", "save");
      const doneBtn = el("button", "primary", "\u4FDD\u5B58\u5E76\u8FD4\u56DE\u5E73\u53F0");
      doneBtn.setAttribute("data-role", "done");
      const exitBtn = el("button", "", "\u9000\u51FA\u7F16\u8F91");
      this.bar.append(logo, title, tag, sp, undoBtn, revertBtn, saveBtn, doneBtn, exitBtn);
      this.root.append(this.bar);
      undoBtn.onclick = () => void this.undo();
      revertBtn.onclick = () => void this.revertAll();
      saveBtn.onclick = () => void this.save();
      doneBtn.onclick = () => void this.saveAndReturn();
      exitBtn.onclick = () => this.destroy();
    }
    /** 保存后直接回到平台，避免用户"保存完不知道回哪去" */
    async saveAndReturn() {
      const ok = await this.save();
      if (!ok) return;
      const back = `${this.opts.session.platformOrigin}/#/experiments/${encodeURIComponent(this.opts.session.experimentId)}`;
      this.toast("\u5DF2\u4FDD\u5B58\uFF0C\u6B63\u5728\u8FD4\u56DE\u5E73\u53F0\u2026");
      window.setTimeout(() => {
        location.href = back;
      }, 600);
    }
    buildList() {
      const wrap = el("div", "ep-ed-list");
      const header = el("header");
      const title = el("b", "", "\u9875\u9762\u5143\u7D20");
      this.listMeta = el("div", "meta", "\u6B63\u5728\u626B\u63CF\u2026");
      const search = el("div", "search");
      const input = el("input");
      input.type = "text";
      input.placeholder = "\u641C\u7D22\u6587\u6848 / \u5143\u7D20 ID\u2026";
      input.oninput = () => this.renderList(input.value.trim().toLowerCase());
      search.append(input);
      header.append(title, this.listMeta, search);
      this.listBody = el("div", "body");
      wrap.append(header, this.listBody);
      this.root.append(wrap);
    }
    buildPanel() {
      this.panel = el("div", "ep-ed-panel");
      this.panel.innerHTML = `<h4>\u9009\u4E00\u4E2A\u5143\u7D20</h4>
      <p class="sub">\u5728\u9875\u9762\u6216\u5DE6\u4FA7\u6E05\u5355\u91CC\u70B9\u4E00\u4E0B</p>
      <div class="ep-ed-empty">\u53EF\u7F16\u8F91\u7684\u533A\u5757\u4F1A\u5728\u60AC\u505C\u65F6\u9AD8\u4EAE\uFF1B\u70B9\u5DE6\u4FA7\u6E05\u5355\u91CC\u7684\u6761\u76EE\uFF0C\u9875\u9762\u4F1A\u81EA\u52A8\u6EDA\u5230\u8BE5\u5143\u7D20\u5E76\u95EA\u70C1\u63D0\u793A\u3002</div>`;
      this.root.append(this.panel);
    }
    /* ------------------------------------------------------------------ 扫描 */
    scan() {
      const groups = /* @__PURE__ */ new Map();
      for (const { targetId, element } of this.opts.anchors()) {
        if (!targetId) continue;
        const protectedNode = element.matches(GUARD_SELECTOR) || Boolean(element.closest(GUARD_SELECTOR));
        const tag = element.tagName;
        let kind = "other";
        if (tag === "IMG") kind = "image";
        else if (tag === "A") kind = "link";
        else if (/^H[1-6]$/.test(tag)) kind = "heading";
        else if (tag === "BUTTON") kind = "button";
        else if (tag === "DIV" || tag === "SPAN" || tag === "P" || tag === "LI") kind = "text";
        const sectionEl = element.closest('[id^="shopify-section-"]');
        const section = (sectionEl?.id ?? "page").replace(/^shopify-section-/, "").replace(/-[0-9a-f]{8}$/i, "") || "page";
        const item = {
          targetId,
          element,
          kind,
          text: textOf(element),
          src: tag === "IMG" ? element.getAttribute("src") : null,
          href: tag === "A" ? element.getAttribute("href") : null,
          protectedNode,
          section
        };
        if (!groups.has(section)) groups.set(section, []);
        groups.get(section).push(item);
      }
      this.scanned = [...groups.values()].flat();
      const editable = this.scanned.filter((s) => !s.protectedNode).length;
      this.listMeta.textContent = `${this.scanned.length} \u4E2A\u951A\u70B9 \xB7 ${editable} \u4E2A\u53EF\u7F16\u8F91 \xB7 ${groups.size} \u4E2A\u533A\u5757`;
      this.renderList();
    }
    /* ------------------------------------------------------------- 清单渲染 */
    renderList(filter = "") {
      const items = filter ? this.scanned.filter((s) => `${s.text} ${s.targetId} ${s.section}`.toLowerCase().includes(filter)) : this.scanned;
      if (items.length === 0) {
        this.listBody.innerHTML = '<div class="ep-ed-empty" style="padding:10px">\u6CA1\u6709\u5339\u914D\u7684\u5143\u7D20</div>';
        return;
      }
      const groups = /* @__PURE__ */ new Map();
      for (const item of items) {
        if (!groups.has(item.section)) groups.set(item.section, []);
        groups.get(item.section).push(item);
      }
      const changedIds = this.changedTargetIds();
      this.listBody.innerHTML = [...groups.entries()].map(([section, list]) => `
        <div class="grp">${escapeHtml(section)}</div>
        ${list.map((s) => {
        const k = s.protectedNode ? "guard" : s.kind === "heading" ? "h" : s.kind === "image" ? "img" : s.kind === "link" ? "link" : s.kind === "button" ? "btn" : "txt";
        const label = s.protectedNode ? "\u9501\u5B9A" : kindLabel(s.kind);
        const changed = changedIds.has(s.targetId) ? " changed" : "";
        const active = this.selected?.targetId === s.targetId ? " active" : "";
        const preview = s.text || s.src || s.href || "\uFF08\u7A7A\uFF09";
        return `<div class="ep-ed-item${changed}${active}" data-target="${escapeAttr(s.targetId)}">
              <span class="k ${k}">${label}</span>
              <span style="min-width:0"><span class="txt">${escapeHtml(preview.slice(0, 60))}</span><span class="id">${escapeHtml(s.targetId)}</span></span>
            </div>`;
      }).join("")}`).join("");
      this.listBody.querySelectorAll("[data-target]").forEach((node) => {
        node.onclick = () => {
          const targetId = node.dataset.target ?? "";
          const item = this.scanned.find((s) => s.targetId === targetId);
          if (item) this.selectItem(item, { scroll: true });
        };
      });
    }
    /* ------------------------------------------------------------- 交互绑定 */
    anchorOf(target) {
      const start = target;
      if (!start?.closest) return null;
      const node = start.closest("[data-ep-id]");
      if (!node) return null;
      return { id: node.getAttribute("data-ep-id") ?? "", el: node };
    }
    bindHover() {
      document.addEventListener("mousemove", this.onMove, true);
    }
    bindClick() {
      document.addEventListener("click", this.onClick, true);
    }
    /**
     * 滚动/缩放时要重新计算浮层位置。
     * 少了这一步，点左侧清单后页面滚走了，选中框会停在原处，用户就"看不出选中了哪里"。
     */
    bindReposition() {
      const sync = () => {
        if (!this.mounted) return;
        if (this.selected) {
          this.place(this.selBox, this.selected.element);
          if (this.changedTargetIds().has(this.selected.targetId)) this.place(this.modBox, this.selected.element);
        }
        if (this.hoverEl && this.hoverEl !== this.selected?.element) this.place(this.hl, this.hoverEl);
      };
      window.addEventListener("scroll", sync, { passive: true, capture: true });
      window.addEventListener("resize", sync);
      window.setInterval(sync, 400);
    }
    /**
     * 把浮层框对齐到元素上。
     * `.ep-ed-root` 是 `position:fixed`（视口坐标系），所以必须用**视口坐标**
     * （getBoundingClientRect 原值）；早先误加了 scrollX/scrollY，页面一滚动框就漂走，
     * 表现就是"选中了却看不出来、改完也看不到改在哪"。
     */
    place(box, node) {
      const r = node.getBoundingClientRect();
      box.style.display = "block";
      box.style.left = `${r.left}px`;
      box.style.top = `${r.top}px`;
      box.style.width = `${Math.max(r.width, 4)}px`;
      box.style.height = `${Math.max(r.height, 4)}px`;
    }
    selectItem(item, { scroll }) {
      this.selected = item;
      if (scroll) {
        item.element.scrollIntoView({ behavior: "smooth", block: "center" });
        window.setTimeout(() => this.flash(item.element), 320);
      }
      window.setTimeout(() => {
        this.place(this.selBox, item.element);
        const lbl2 = el("div", "lbl", `${kindLabel(item.kind)} \xB7 ${item.targetId}`);
        this.selBox.querySelectorAll(".lbl").forEach((n) => n.remove());
        this.selBox.appendChild(lbl2);
      }, scroll ? 340 : 0);
      this.place(this.selBox, item.element);
      const lbl = el("div", "lbl", `${kindLabel(item.kind)} \xB7 ${item.targetId}`);
      this.selBox.querySelectorAll(".lbl").forEach((n) => n.remove());
      this.selBox.appendChild(lbl);
      this.hl.style.display = "none";
      this.renderList();
      this.renderPanel();
    }
    flash(node) {
      node.classList.remove("ep-ed-flash");
      void node.offsetWidth;
      node.classList.add("ep-ed-flash");
      window.setTimeout(() => node.classList.remove("ep-ed-flash"), 2e3);
    }
    /* --------------------------------------------------------------- 已改标记 */
    changedTargetIds() {
      const set = /* @__PURE__ */ new Set();
      for (const op of this.ops) {
        const id = op.target?.targetId;
        if (id) set.add(id);
      }
      return set;
    }
    syncModifiedMarks() {
      const changed = this.changedTargetIds();
      const target = this.selected && changed.has(this.selected.targetId) ? this.selected.element : null;
      if (!target) {
        this.modBox.style.display = "none";
        return;
      }
      this.place(this.modBox, target);
    }
    /* ------------------------------------------------------------- 面板渲染 */
    renderPanel() {
      const item = this.selected;
      if (!item) return;
      const kind = kindLabel(item.kind);
      if (item.protectedNode) {
        this.panel.innerHTML = `<h4>${escapeHtml(kind)} <span class="kindtag" style="background:#c0392b">\u53D7\u4FDD\u62A4</span></h4>
        <p class="sub">${escapeHtml(item.targetId)}</p>
        <div class="ep-ed-guard">\u4EF7\u683C\u3001\u8868\u5355\u3001\u8D2D\u7269\u8F66\u63A7\u4EF6\u4E0D\u5141\u8BB8\u88AB\u5B9E\u9A8C\u6539\u5199\uFF08\u62A4\u680F\u89C4\u5219\uFF0C\u7EBF\u4E0A\u8FD0\u884C\u65F6\u540C\u6837\u4F1A\u62E6\u622A\uFF09\u3002</div>
        ${this.opsSection()}`;
        this.bindOps();
        return;
      }
      const isImg = item.kind === "image";
      const styles = getComputedStyle(item.element);
      const changed = this.changedTargetIds().has(item.targetId);
      this.panel.innerHTML = `
      <h4>${escapeHtml(kind)} <span class="kindtag">${escapeHtml(item.targetId.slice(0, 22))}</span>${changed ? '<span class="kindtag" style="background:#e17055">\u5DF2\u4FEE\u6539</span>' : ""}</h4>
      <p class="sub">${escapeHtml(item.targetId)}</p>

      ${isImg ? `<label>\u56FE\u7247\u5730\u5740<input type="text" id="epImgUrl" value="${escapeAttr(item.src ?? "")}"></label>` : `<label>\u6587\u6848\u5185\u5BB9<textarea id="epText" rows="3">${escapeHtml(item.text)}</textarea></label>`}

      ${item.kind === "link" || item.element.querySelector("a") ? `<label>\u94FE\u63A5\u5730\u5740<input type="text" id="epHref" value="${escapeAttr(item.href ?? item.element.querySelector("a")?.getAttribute("href") ?? "")}"></label>` : ""}

      <div class="row">
        <label>\u6587\u5B57\u989C\u8272<input type="color" id="epColor" value="${toHex(styles.color)}"></label>
        <label>\u5B57\u53F7 px<input type="number" id="epSize" min="8" max="96" placeholder="\u4E0D\u53D8"></label>
      </div>

      <div class="acts">
        <button class="primary" id="epApply">\u5E94\u7528\u5230\u9875\u9762</button>
        <button id="epLocate">\u5B9A\u4F4D\u95EA\u70C1</button>
        <button id="epHide">\u9690\u85CF</button>
        <button id="epShow">\u663E\u793A</button>
      </div>
      <div class="ep-ed-live" id="epLive">\u6539\u52A8**\u5373\u65F6\u751F\u6548**\uFF1B\u786E\u8BA4\u65E0\u8BEF\u540E\u70B9\u9876\u90E8\u300C\u4FDD\u5B58\u5230 Variant\u300D\u624D\u4F1A\u5199\u8FDB\u5B9E\u9A8C</div>
      ${this.opsSection()}`;
      this.panel.querySelector("#epApply").onclick = () => void this.applyFromPanel();
      this.panel.querySelector("#epLocate").onclick = () => {
        item.element.scrollIntoView({ behavior: "smooth", block: "center" });
        window.setTimeout(() => this.flash(item.element), 320);
      };
      this.panel.querySelector("#epHide").onclick = () => void this.apply(mkOp("visibility.hide", item));
      this.panel.querySelector("#epShow").onclick = () => void this.apply(mkOp("visibility.show", item));
      this.bindOps();
      this.bindLiveInputs(item);
    }
    /**
     * 实时预览：边打字边把改动应用到页面（Ptengine / VWO 都是这个行为）。
     * 之前的版本要用户自己点「应用到页面」才算改，很多人根本不知道要点，
     * 于是出现"我改了内容但页面没变"。
     */
    bindLiveInputs(item) {
      const debounce = (fn, ms) => {
        let t;
        return () => {
          if (t !== void 0) window.clearTimeout(t);
          t = window.setTimeout(fn, ms);
        };
      };
      const ta = this.panel.querySelector("#epText");
      if (ta) {
        const run = debounce(() => void this.upsertAndApply(mkOp("text.replace", item, { before: textOf(item.element), after: ta.value }), "text"), 320);
        ta.addEventListener("input", run);
      }
      const img = this.panel.querySelector("#epImgUrl");
      if (img) {
        const fire = () => {
          if (img.value.trim()) void this.upsertAndApply(mkOp("image.replace", item, { after: img.value.trim() }), "image");
        };
        img.addEventListener("change", fire);
        img.addEventListener("keydown", (ev) => {
          if (ev.key === "Enter") fire();
        });
      }
      const href = this.panel.querySelector("#epHref");
      if (href) {
        const fire = () => {
          if (href.value.trim()) void this.upsertAndApply(mkOp("link.set", item, { before: item.element.getAttribute("href") ?? "", after: href.value.trim() }), "href");
        };
        href.addEventListener("change", fire);
      }
      const color = this.panel.querySelector("#epColor");
      if (color) color.addEventListener("input", debounce(() => void this.applyFromPanel(true), 260));
      const size = this.panel.querySelector("#epSize");
      if (size) size.addEventListener("input", debounce(() => void this.applyFromPanel(true), 320));
    }
    /**
     * 队列里的"同类同目标"操作做覆盖写（而不是无限追加），
     * 这样边打字边预览不会在操作队列里堆出 30 条废操作。
     */
    async upsertAndApply(op, kind) {
      const item = this.selected;
      if (!item) return;
      const targetId = item.targetId;
      this.ops = this.ops.filter((o) => {
        const sameTarget = o.target?.targetId === targetId;
        if (!sameTarget) return true;
        return o.type !== op.type;
      });
      try {
        const res = await this.opts.applyOperation(op);
        if (res.status === "applied") {
          this.ops.push(op);
          this.renderOps();
          this.renderList();
          this.place(this.selBox, item.element);
          this.place(this.modBox, item.element);
          const live = this.panel.querySelector("#epLive");
          if (live) live.innerHTML = `\u5DF2\u5B9E\u65F6\u5E94\u7528\u5230\u9875\u9762\uFF08<b>${this.ops.length}</b> \u6761\u64CD\u4F5C\uFF09\xB7 \u70B9\u9876\u90E8\u300C\u4FDD\u5B58\u5230 Variant\u300D\u5199\u8FDB\u5B9E\u9A8C`;
        } else {
          this.toast(`\u88AB\u963B\u65AD\uFF1A${res.status}${res.reason ? " / " + res.reason : ""}`, true);
        }
      } catch (err) {
        this.toast(`\u5E94\u7528\u5931\u8D25\uFF1A${err.message}`, true);
      }
      void kind;
    }
    opsSection() {
      const list = this.ops.length ? this.ops.slice().reverse().map((o, i) => {
        const targetId = o.target?.targetId ?? "";
        const detail = o.after ? String(o.after).slice(0, 24) : o.styles ? JSON.stringify(o.styles).slice(0, 24) : "";
        return `<div class="op" data-locate="${escapeAttr(targetId)}" data-idx="${this.ops.length - 1 - i}">
              <span class="mono">${escapeHtml(String(o.type))}</span> \xB7 ${escapeHtml(targetId)}${detail ? ` \u2192 ${escapeHtml(detail)}` : ""}</div>`;
      }).join("") : '<div class="ep-ed-empty">\u8FD8\u6CA1\u6709\u64CD\u4F5C</div>';
      return `<div class="ep-ed-ops"><div class="ttl">\u64CD\u4F5C\u961F\u5217\uFF08\u70B9\u6761\u76EE\u53EF\u5B9A\u4F4D\uFF09</div>${list}</div>`;
    }
    bindOps() {
      this.panel.querySelectorAll("[data-locate]").forEach((node) => {
        node.onclick = () => {
          const targetId = node.dataset.locate ?? "";
          const item = this.scanned.find((s) => s.targetId === targetId);
          if (item) this.selectItem(item, { scroll: true });
          else this.toast(`\u9875\u9762\u4E0A\u627E\u4E0D\u5230 ${targetId}\uFF08\u53EF\u80FD\u4E0D\u5728\u5F53\u524D\u9875\u9762\uFF09`, true);
        };
      });
    }
    /* --------------------------------------------------------------- 应用操作 */
    async applyFromPanel(silent = false) {
      const item = this.selected;
      if (!item) return;
      const created = [];
      const img = this.panel.querySelector("#epImgUrl");
      const text = this.panel.querySelector("#epText");
      const href = this.panel.querySelector("#epHref");
      const color = this.panel.querySelector("#epColor");
      const size = this.panel.querySelector("#epSize");
      const live = getComputedStyle(item.element);
      if (img && img.value.trim() && img.value.trim() !== (item.src ?? "")) {
        created.push(mkOp("image.replace", item, { after: img.value.trim() }));
      }
      if (text && text.value.trim() !== textOf(item.element)) {
        created.push(mkOp("text.replace", item, { before: textOf(item.element), after: text.value }));
      }
      if (href && href.value.trim()) {
        const current = item.element.getAttribute("href") ?? item.element.querySelector("a")?.getAttribute("href") ?? "";
        if (href.value.trim() !== current) created.push(mkOp("link.set", item, { before: current, after: href.value.trim() }));
      }
      const stylePatch = {};
      if (color && toHex(live.color) !== color.value.toLowerCase()) stylePatch.color = color.value;
      if (size && size.value && Math.round(parseFloat(live.fontSize)) !== Number(size.value)) stylePatch["font-size"] = `${size.value}px`;
      if (Object.keys(stylePatch).length) created.push(mkOp("style.set", item, { styles: stylePatch }));
      if (created.length === 0) {
        if (!silent) this.toast("\u6CA1\u6709\u68C0\u6D4B\u5230\u4FEE\u6539\uFF08\u628A\u5185\u5BB9\u6539\u6389\u518D\u70B9\u5E94\u7528\uFF09", true);
        return;
      }
      for (const op of created) await this.apply(op);
    }
    async apply(op) {
      try {
        const res = await this.opts.applyOperation(op);
        if (res.status === "applied") {
          this.ops.push(op);
          this.renderOps();
          this.renderList();
          this.renderPanel();
          const item = this.selected;
          if (item) {
            this.place(this.selBox, item.element);
            this.place(this.modBox, item.element);
          }
          this.toast(`\u5DF2\u5E94\u7528 ${String(op.type)} \u2014\u2014 \u9875\u9762\u4E0A\u5DF2\u751F\u6548\uFF0C\u70B9\u4E86\u300C\u4FDD\u5B58\u5230 Variant\u300D\u624D\u4F1A\u5199\u8FDB\u5B9E\u9A8C`);
        } else {
          this.toast(`\u88AB\u963B\u65AD\uFF1A${res.status}${res.reason ? " / " + res.reason : ""}`, true);
        }
      } catch (err) {
        this.toast(`\u5E94\u7528\u5931\u8D25\uFF1A${err.message}`, true);
      }
    }
    async undo() {
      if (this.ops.length === 0) return this.toast("\u6CA1\u6709\u53EF\u64A4\u9500\u7684\u64CD\u4F5C", true);
      this.ops.pop();
      await this.replayAll();
      this.renderOps();
      this.renderList();
      if (this.selected) this.renderPanel();
      this.toast("\u5DF2\u64A4\u9500\u4E0A\u4E00\u6B65");
    }
    async revertAll() {
      await this.opts.revert();
      this.ops = [];
      this.renderOps();
      this.renderList();
      this.modBox.style.display = "none";
      this.selected = null;
      this.selBox.style.display = "none";
      this.panel.innerHTML = '<h4>\u9009\u4E00\u4E2A\u5143\u7D20</h4><p class="sub">\u9875\u9762\u5DF2\u6062\u590D\u5230\u4E3B\u9898\u539F\u59CB\u72B6\u6001</p>';
      this.toast("\u5DF2\u6062\u590D\u539F\u6837");
    }
    async replayAll() {
      await this.opts.revert();
      for (const op of this.ops) {
        try {
          await this.opts.applyOperation(op);
        } catch {
        }
      }
    }
    async save() {
      if (this.saving) return false;
      this.saving = true;
      const saveBtn = this.bar.querySelector('[data-role="save"]');
      const doneBtn = this.bar.querySelector('[data-role="done"]');
      if (saveBtn) {
        saveBtn.disabled = true;
        saveBtn.textContent = "\u4FDD\u5B58\u4E2D\u2026";
      }
      if (doneBtn) doneBtn.disabled = true;
      try {
        const res = await fetch(`${this.opts.session.platformOrigin}/api/v1/preview-sessions/${encodeURIComponent(this.opts.session.previewSessionId)}/operations`, {
          method: "POST",
          headers: { "content-type": "application/json", authorization: `Bearer ${this.opts.token}` },
          body: JSON.stringify({ operations: this.ops })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message ?? `HTTP ${res.status}`);
        this.toast(`\u5DF2\u4FDD\u5B58 ${this.ops.length} \u6761\u64CD\u4F5C\u5230 Variant ${this.opts.session.variantId}`);
        return true;
      } catch (err) {
        this.toast(`\u4FDD\u5B58\u5931\u8D25\uFF1A${err.message}`, true);
        return false;
      } finally {
        this.saving = false;
        if (saveBtn) {
          saveBtn.disabled = false;
          saveBtn.textContent = "\u4FDD\u5B58\u5230 Variant";
        }
        if (doneBtn) doneBtn.disabled = false;
      }
    }
    /* ---------------------------------------------------------------- 小工具 */
    renderOps() {
      const tag = this.bar.querySelector('[data-role="tag"]');
      if (tag) tag.textContent = `Variant ${this.opts.session.variantId} \xB7 ${this.ops.length} \u6761\u64CD\u4F5C`;
      const box = this.panel.querySelector(".ep-ed-ops");
      if (box) box.outerHTML = this.opsSection();
      this.bindOps();
    }
    toast(msg, isErr = false) {
      this.toastEl?.remove();
      const t = el("div", `ep-ed-toast${isErr ? " err" : ""}`, msg);
      this.root.appendChild(t);
      this.toastEl = t;
      window.setTimeout(() => {
        t.remove();
        if (this.toastEl === t) this.toastEl = null;
      }, 3600);
    }
  };
  function mkOp(type, item, extra = {}) {
    return {
      id: `ipe_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      type,
      sequence: Date.now() % 1e5,
      target: {
        targetId: item.targetId,
        locatorVersion: 1,
        selector: `[data-ep-id="${item.targetId}"]`,
        expectMatchCount: 1,
        adapter: "focal-export+platform.1"
      },
      ...extra
    };
  }
  function kindLabel(kind) {
    switch (kind) {
      case "heading":
        return "\u6807\u9898";
      case "image":
        return "\u56FE\u7247";
      case "link":
        return "\u94FE\u63A5";
      case "button":
        return "\u6309\u94AE";
      case "text":
        return "\u6587\u672C";
      default:
        return "\u5143\u7D20";
    }
  }
  function textOf(node) {
    const clone = node.cloneNode(true);
    clone.querySelectorAll("img,svg,script,style").forEach((n) => n.remove());
    return (clone.textContent ?? "").replace(/\s+/g, " ").trim();
  }
  function escapeHtml(s) {
    return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  }
  function escapeAttr(s) {
    return escapeHtml(s);
  }
  function toHex(rgb) {
    const m = /rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/.exec(rgb ?? "");
    if (!m) return "#2a2422";
    const hex = (n) => Number(n).toString(16).padStart(2, "0");
    return `#${hex(m[1])}${hex(m[2])}${hex(m[3])}`;
  }

  // poc/preview-bridge/protocol.ts
  var PROTOCOL_VERSION = "1.0";
  var MAX_PAYLOAD_BYTES = 64 * 1024;
  function payloadBytes(payload) {
    let json;
    try {
      json = JSON.stringify(payload ?? null);
    } catch {
      return Number.POSITIVE_INFINITY;
    }
    if (typeof TextEncoder === "function") return new TextEncoder().encode(json).length;
    return json.length;
  }
  function safeEqual(a, b) {
    if (typeof a !== "string" || typeof b !== "string") return false;
    if (a.length !== b.length) return false;
    let diff = 0;
    for (let i = 0; i < a.length; i += 1) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
    return diff === 0;
  }
  function validateEnvelope(raw, opts) {
    if (!safeEqual(opts.actualOrigin || "", opts.expectedOrigin || "")) {
      return { ok: false, code: "E_ORIGIN_NOT_ALLOWED", detail: `origin ${opts.actualOrigin}` };
    }
    if (!raw || typeof raw !== "object") return { ok: false, code: "E_INVALID_PAYLOAD", detail: "not an object" };
    const env = raw;
    if (payloadBytes(env.payload) > MAX_PAYLOAD_BYTES) {
      return { ok: false, code: "E_PAYLOAD_TOO_LARGE", detail: `> ${MAX_PAYLOAD_BYTES}B` };
    }
    if (env.protocolVersion !== PROTOCOL_VERSION) {
      return { ok: false, code: "E_PROTOCOL_VERSION", detail: String(env.protocolVersion) };
    }
    if (typeof env.type !== "string" || !opts.allowedTypes.includes(env.type)) {
      return { ok: false, code: "E_UNKNOWN_TYPE", detail: String(env.type) };
    }
    if (typeof env.requestId !== "string" || env.requestId.length < 4 || env.requestId.length > 80) {
      return { ok: false, code: "E_INVALID_PAYLOAD", detail: "requestId" };
    }
    if (!safeEqual(String(env.previewSessionId ?? ""), opts.expectedSessionId)) {
      return { ok: false, code: "E_BAD_NONCE", detail: "session mismatch" };
    }
    const now = opts.now ?? Date.now();
    if (now > opts.sessionExpiresAt) return { ok: false, code: "E_SESSION_EXPIRED" };
    if (!safeEqual(String(env.nonce ?? ""), opts.expectedNonce)) {
      return { ok: false, code: "E_BAD_NONCE", detail: "nonce mismatch" };
    }
    return { ok: true, envelope: env };
  }
  var FORBIDDEN_KEYS = ["script", "code", "eval", "expression", "queryselector", "exec", "function", "javascript"];
  function containsForbiddenKeys(value, depth = 0) {
    if (depth > 6 || value === null || typeof value !== "object") return null;
    for (const [k, v] of Object.entries(value)) {
      if (FORBIDDEN_KEYS.includes(k.toLowerCase())) return k;
      const nested = containsForbiddenKeys(v, depth + 1);
      if (nested) return nested;
    }
    return null;
  }
  function buildEnvelope(type, requestId, previewSessionId, nonce, payload) {
    return {
      protocolVersion: PROTOCOL_VERSION,
      type,
      requestId,
      previewSessionId,
      nonce,
      sentAt: Date.now(),
      ...payload === void 0 ? {} : { payload }
    };
  }

  // poc/preview-bridge/preview.ts
  var APPLY_CAPABILITY = [
    "text.replace",
    "style.set",
    "link.set",
    "image.replace",
    "visibility.hide",
    "visibility.show"
  ];
  var PreviewBridgeAgent = class {
    constructor(session, pageSnapshotId) {
      this.session = session;
      this.pageSnapshotId = pageSnapshotId;
      this.selectMode = false;
      this.selectHandler = null;
      this.ready = false;
      /** 页面内编辑器（`ep_editor=1` 时挂载） */
      this.editor = null;
      this.stats = { received: 0, accepted: 0, rejected: 0, byCode: {}, applied: 0, reverted: 0 };
      this.log = [];
      this.onMessage = (event) => {
        this.stats.received += 1;
        const result = validateEnvelope(event.data, {
          allowedTypes: ["HOST_HELLO", "SELECT", "APPLY_OPERATION", "REVERT", "SNAPSHOT_REQUEST", "HEARTBEAT"],
          expectedOrigin: this.session.parentOrigin,
          actualOrigin: event.origin,
          expectedNonce: this.session.nonce,
          expectedSessionId: this.session.previewSessionId,
          sessionExpiresAt: this.session.expiresAt
        });
        if (!result.ok || !result.envelope) {
          const raw = event.data;
          this.reject(result.code ?? "E_INVALID_PAYLOAD", result.detail, typeof raw?.requestId === "string" ? raw.requestId : void 0);
          return;
        }
        const env = result.envelope;
        this.stats.accepted += 1;
        switch (env.type) {
          case "HOST_HELLO": {
            this.ready = true;
            this.reply("PREVIEW_READY", env.requestId, {
              protocolVersion: "1.0",
              capability: APPLY_CAPABILITY,
              pageSnapshotId: this.pageSnapshotId,
              viewport: { width: window.innerWidth, height: window.innerHeight, dpr: window.devicePixelRatio },
              documentHeight: Math.round(document.documentElement.scrollHeight)
            });
            return;
          }
          case "SELECT": {
            this.enableSelectMode();
            this.reply("ELEMENT_SELECTED", env.requestId, { mode: "armed", hint: "click an element in the preview" });
            return;
          }
          case "APPLY_OPERATION": {
            const payload = env.payload;
            const op = payload?.operation;
            if (!op || typeof op !== "object" || typeof op.id !== "string" || typeof op.target?.selector !== "string") {
              this.reject("E_INVALID_PAYLOAD", "operation shape", env.requestId);
              return;
            }
            const forbidden = containsForbiddenKeys(op);
            if (forbidden) {
              this.reject("E_UNSAFE_OPERATION", `forbidden key "${forbidden}"`, env.requestId);
              return;
            }
            if ((op.type === "link.set" || op.type === "image.replace") && op.after && !isSafeHref(op.after)) {
              this.reject("E_UNSAFE_OPERATION", `unsafe url scheme in ${op.type}`, env.requestId);
              return;
            }
            const results = this.opRuntime.applyAll([op]);
            const result2 = results[0];
            if (result2?.status === "applied") this.stats.applied += 1;
            let textAfter = null;
            if (result2?.status === "applied" && op.type === "text.replace") {
              try {
                const el2 = document.querySelector(op.target.selector);
                textAfter = el2 ? (el2.textContent ?? "").slice(0, 100) : null;
              } catch {
                textAfter = null;
              }
            }
            this.reply("OPERATION_RESULT", env.requestId, {
              opId: op.id,
              status: result2?.status ?? "failed",
              reason: result2?.reason ?? null,
              appliedCount: this.opRuntime.count,
              text_after: textAfter
            });
            return;
          }
          case "REVERT": {
            const reverted = this.opRuntime.revertAll();
            this.stats.reverted += reverted;
            this.reply("REVERT_RESULT", env.requestId, { reverted, appliedCount: this.opRuntime.count });
            return;
          }
          case "SNAPSHOT_REQUEST": {
            const GUARDED = '.price, [class*="price" i], [data-price], input, select, textarea, form, [name="add"], button[name="add"]';
            const targets = [...document.querySelectorAll("[data-ep-id]")].slice(0, 300).map((el2) => {
              const protectedNode = el2.matches(GUARDED) || !!el2.closest(GUARDED);
              const isText = /^(H[1-6]|P|SPAN|A|BUTTON|DIV|LI)$/.test(el2.tagName);
              const caps = [];
              if (isText && (el2.textContent ?? "").trim()) caps.push("text.replace");
              caps.push("style.set");
              if (el2.tagName === "A") caps.push("link.set");
              if (el2.tagName === "IMG") {
                caps.length = 0;
                caps.push("image.replace");
              }
              caps.push("visibility.hide");
              return {
                targetId: el2.getAttribute("data-ep-id"),
                locatorVersion: Number(el2.getAttribute("data-ep-version") ?? 1),
                nodeKind: el2.tagName.toLowerCase(),
                text: (el2.textContent ?? "").replace(/\s+/g, " ").trim().slice(0, 120),
                href: el2.tagName === "A" ? el2.getAttribute("href") : null,
                src: el2.tagName === "IMG" ? el2.getAttribute("src") : null,
                protected: protectedNode,
                visible: !!(el2.offsetWidth || el2.offsetHeight || el2.getClientRects().length),
                capability: protectedNode ? [] : caps,
                sectionId: el2.closest('[id^="shopify-section-"]')?.id ?? null
              };
            });
            this.reply("SNAPSHOT_READY", env.requestId, {
              pageSnapshotId: this.pageSnapshotId,
              url: location.href.split("?")[0],
              viewport: { width: window.innerWidth, height: window.innerHeight, dpr: window.devicePixelRatio },
              documentHeight: Math.round(document.documentElement.scrollHeight),
              appliedCount: this.opRuntime.count,
              targetCount: targets.length,
              targets
            });
            return;
          }
          case "HEARTBEAT": {
            const payload = env.payload;
            this.reply("HEARTBEAT_ACK", env.requestId, { t: payload?.t ?? null, serverTime: Date.now() });
            return;
          }
          default: {
            this.reject("E_UNKNOWN_TYPE", String(env.type), env.requestId);
          }
        }
      };
      this.opRuntime = new OperationRuntime(deviceType(), (name, props) => {
        this.log.unshift({ at: Date.now(), type: name, detail: JSON.stringify(props).slice(0, 120) });
      });
      window.addEventListener("message", this.onMessage);
    }
    /**
     * 挂载页面内浮动编辑器（Ptengine / VWO 的做法）：编辑直接在店铺页面上完成，
     * 不再依赖"平台页 + 弹窗 + postMessage"的双窗口往返。
     * 操作仍走同一个 OperationRuntime，护栏规则与线上一致；保存用 preview token 落库。
     */
    async mountInPageEditor(options) {
      if (this.editor) return;
      const editor = new InPageEditor({
        session: {
          previewSessionId: this.session.previewSessionId,
          experimentId: this.session.experimentId,
          variantId: this.session.variantId,
          platformOrigin: options.platformOrigin.replace(/\/$/, "")
        },
        token: options.token,
        applyOperation: async (op) => {
          const results = this.opRuntime.applyAll([op]);
          const r = results[0];
          if (r?.status === "applied") this.stats.applied += 1;
          return { status: r?.status ?? "failed", reason: r?.reason };
        },
        revert: async () => {
          const reverted = this.opRuntime.revertAll();
          this.stats.reverted += reverted;
        },
        anchors: () => [...document.querySelectorAll("[data-ep-id]")].map((element) => ({
          targetId: element.getAttribute("data-ep-id") ?? "",
          element
        }))
      });
      await editor.mount();
      this.editor = editor;
      document.documentElement.setAttribute("data-ep-editor", "mounted");
    }
    reply(type, requestId, payload) {
      const env = buildEnvelope(type, requestId, this.session.previewSessionId, this.session.nonce, payload);
      const channel = window.opener && window.opener !== window ? window.opener : window.parent;
      channel.postMessage(env, this.session.parentOrigin);
    }
    reject(code, detail, requestId) {
      this.stats.rejected += 1;
      this.stats.byCode[code] = (this.stats.byCode[code] ?? 0) + 1;
      this.log.unshift({ at: Date.now(), type: "reject", code, detail });
      try {
        void fetch("/api/bridge-audit", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ code, detail: detail ?? null, at: Date.now(), sessionId: this.session.previewSessionId }),
          keepalive: true
        });
      } catch {
      }
      if (code === "E_ORIGIN_NOT_ALLOWED") return;
      if (requestId) this.reply("ERROR", requestId, { code, detail: detail ?? null });
    }
    enableSelectMode() {
      if (this.selectMode) return;
      this.selectMode = true;
      this.selectHandler = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const target = ev.target instanceof Element ? ev.target : null;
        const host = target?.closest("[data-ep-id]") ?? null;
        const info = {
          targetId: host?.getAttribute("data-ep-id") ?? null,
          locatorVersion: Number(host?.getAttribute("data-ep-version") ?? 1),
          nodeKind: host?.tagName.toLowerCase() ?? null,
          tagPath: host ? [host.tagName.toLowerCase(), ...target && target !== host ? [target.tagName.toLowerCase()] : []] : [],
          canEdit: !!host && !host.hasAttribute("data-ep-protected"),
          capability: host && !host.hasAttribute("data-ep-protected") ? APPLY_CAPABILITY.slice(0, 3) : []
        };
        this.reply("ELEMENT_SELECTED", `sel_${Date.now().toString(36)}`, info);
        this.disableSelectMode();
      };
      document.addEventListener("click", this.selectHandler, true);
    }
    disableSelectMode() {
      this.selectMode = false;
      if (this.selectHandler) document.removeEventListener("click", this.selectHandler, true);
      this.selectHandler = null;
    }
    get isReady() {
      return this.ready;
    }
    dispose() {
      this.disableSelectMode();
      window.removeEventListener("message", this.onMessage);
    }
  };
  return __toCommonJS(preview_exports);
})();
